import jsPDF from 'jspdf'

export type ExportColumn = {
  key: string
  label: string
  align?: 'left' | 'right'
}
export type ExportRow = Record<string, string | number>

const download = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const cell = (value: string | number | undefined) =>
  typeof value === 'number' ? value.toFixed(2) : (value ?? '')

export const safeFilename = (value: string) =>
  value.replace(/[^a-z0-9-_]+/gi, '_').replace(/_+/g, '_')

export function exportCsv(filename: string, columns: ExportColumn[], rows: ExportRow[]) {
  const escape = (value: string) =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
  const lines = [
    columns.map((c) => escape(c.label)).join(','),
    ...rows.map((row) => columns.map((c) => escape(String(cell(row[c.key])))).join(',')),
  ]
  download(new Blob([`\ufeff${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' }), `${safeFilename(filename)}.csv`)
}

/** Excel-compatible workbook (SpreadsheetML table) — opens directly in Excel. */
export function exportExcel(filename: string, title: string, columns: ExportColumn[], rows: ExportRow[]) {
  const esc = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const head = columns.map((c) => `<th style="background:#e2e8f0;text-align:left">${esc(c.label)}</th>`).join('')
  const body = rows
    .map(
      (row) =>
        `<tr>${columns
          .map((c) => {
            const value = row[c.key]
            return typeof value === 'number'
              ? `<td style="mso-number-format:'0.00'">${value.toFixed(2)}</td>`
              : `<td style="mso-number-format:'\\@'">${esc(String(value ?? ''))}</td>`
          })
          .join('')}</tr>`,
    )
    .join('')
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><h3>${esc(title)}</h3><table border="1">${`<tr>${head}</tr>`}${body}</table></body></html>`
  download(new Blob([html], { type: 'application/vnd.ms-excel' }), `${safeFilename(filename)}.xls`)
}

/** Landscape A4 table PDF with repeating headers and page numbers. */
export function exportPdf(
  filename: string,
  title: string,
  subtitle: string,
  columns: ExportColumn[],
  rows: ExportRow[],
  totals?: ExportRow,
) {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const pageWidth = 297
  const margin = 10
  const usable = pageWidth - margin * 2
  const colWidth = usable / columns.length
  const rowHeight = 6.5
  const drawHeader = (first: boolean) => {
    let y = margin + 4
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(13)
    pdf.text('BMED MEDICAL CENTRE', margin, y)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.text('Plot No. 1884/10, Along Avic Road, Lilayi Estate, Kafue, Lusaka · +260975202011', margin, y + 4.5)
    y += 11
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(first ? 12 : 10)
    pdf.text(first ? title : `${title} (continued)`, margin, y)
    if (first) {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8.5)
      pdf.text(subtitle, margin, y + 5)
      y += 5
    }
    y += 5
    pdf.setFillColor(226, 232, 240)
    pdf.rect(margin, y, usable, rowHeight, 'F')
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(7.5)
    columns.forEach((column, index) => {
      const x = margin + index * colWidth
      const text = pdf.splitTextToSize(column.label, colWidth - 2)[0]
      if (column.align === 'right') pdf.text(text, x + colWidth - 1, y + 4.3, { align: 'right' })
      else pdf.text(text, x + 1, y + 4.3)
    })
    return y + rowHeight
  }
  const drawRow = (row: ExportRow, y: number, bold = false) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal')
    pdf.setFontSize(7.5)
    columns.forEach((column, index) => {
      const x = margin + index * colWidth
      const raw = row[column.key]
      const value = typeof raw === 'number' ? raw.toLocaleString('en-ZM', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(raw ?? '')
      const text = pdf.splitTextToSize(value, colWidth - 2)[0] ?? ''
      if (column.align === 'right') pdf.text(text, x + colWidth - 1, y + 4.3, { align: 'right' })
      else pdf.text(text, x + 1, y + 4.3)
    })
    pdf.setDrawColor(226, 232, 240)
    pdf.line(margin, y + rowHeight, margin + usable, y + rowHeight)
  }
  let y = drawHeader(true)
  const bottom = 210 - margin - 6
  for (const row of rows) {
    if (y + rowHeight > bottom) {
      pdf.addPage()
      y = drawHeader(false)
    }
    drawRow(row, y)
    y += rowHeight
  }
  if (totals) {
    if (y + rowHeight > bottom) {
      pdf.addPage()
      y = drawHeader(false)
    }
    drawRow(totals, y, true)
  }
  const pages = pdf.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(7)
    pdf.text(`Confidential — HR & Payroll · Generated ${new Date().toLocaleString('en-GB')}`, margin, 210 - margin)
    pdf.text(`Page ${page} of ${pages}`, pageWidth - margin, 210 - margin, { align: 'right' })
  }
  pdf.save(`${safeFilename(filename)}.pdf`)
}
